import React, {useState} from "react";
import {TextField} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import {makeStyles} from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import clsx from "clsx";
import FormLabel from "@material-ui/core/FormLabel";
import {useTranslation} from "react-i18next";
import ListItem from "@material-ui/core/ListItem";
import List from "@material-ui/core/List";
import RemoveCircle from '@material-ui/icons/RemoveCircle';
import {ArrowForwardIos} from "@material-ui/icons";
import FormControl from "@material-ui/core/FormControl";
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckIcon from '@material-ui/icons/Check';
import {FormStatus} from "./form-status";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import Typography from "@material-ui/core/Typography";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import WarningIcon from '@material-ui/icons/Warning';
import {OrganizationUnitSelectorDialog} from "../_shared/organization-unit-selector/OrganizationUnitSelectorDialog";
import {OrgUnit} from "../../models/organization";

const useItemStyles = makeStyles(theme => {
    const disabledOpacity = 0.6;
    return ({
        subscriptionsTransferItem: {
            width: '100%',
            marginBottom: theme.spacing(2)
        },
        form: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            '& > *': {
                margin: theme.spacing(1),
            },
            '& > .MuiFormControl-root:not(.MuiTextField-root)': {
                minWidth: 200,
                flexBasis: '25%',
                flexGrow: 1,
                margin: theme.spacing(1),
                '& > .MuiButton-root': {
                    display: 'flex',
                    justifyContent: 'space-between'
                }
            },
            '& > .MuiTextField-root': {
                width: '80px',
            }
        },
        from: {
            background: (theme.palette as any).dark.background,
            color: (theme.palette as any).dark.text,
            marginTop: theme.spacing(2),
            '&.Mui-disabled': {
                color: (theme.palette as any).dark.text,
                opacity: disabledOpacity
            }
        },
        disabled: {
            opacity: disabledOpacity
        },
        to: {
            marginTop: theme.spacing(2),
            borderColor: (theme.palette as any).dark.line,
            border: 'thin solid',
            '&.Mui-disabled': {
                color: (theme.palette as any).dark.background,
                opacity: disabledOpacity
            }
        },
        remove: {
            margin: 'auto'
        },
        deleteBtn: {
            color: theme.palette.error.main
        },
        error: {
            color: theme.palette.error.light
        },
        fromButtonError: {
            background: theme.palette.error.light,
        },
        toButtonError: {
            border: `thin ${theme.palette.error.light} solid`,
            color: theme.palette.error.light
        },
        actionContainer: {
            width: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        successLabel: {
            color: theme.palette.success.main
        },
        availableSubscriptionsLabel: {
            justifyContent: 'flex-end'
        },
        errorsContainer: {
            width: '100%',
            color: theme.palette.error.main,
            border: 'none',
            'boxShadow': 'none',
            'border-bottom': `2px solid ${theme.palette.error.main}`,
            'borderRadius': '0 !important',
            '& .MuiExpansionPanelSummary-root': {
                padding: 0
            },
            '& .MuiExpansionPanelDetails-root': {
                padding: '8px 0'
            },
            '& .MuiExpansionPanelSummary-content.Mui-expanded': {
                margin: '20px 0'
            }
        }
    });
});
const EditMode = Object.freeze({
    None: 0,
    Source: 1,
    Destination: 2,
});
export const SubscriptionTransfer = ({children, transfer, handleTransferChanged, handleTransferDeleted, handleOrgUnitChanged, organizationId, isOnlyRow}: any) => {
    const classes = useItemStyles();
    const {t} = useTranslation();
    const {id, data, errors} = transfer;
    const [editMode, setEditMode] = useState<number>(EditMode.None);
    const hasError = (field: any) => {
        return errors && !!errors[field];
    };
    const handleInputChange = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        const licenseType = e.target.name;
        const count = e.target.value;
        handleTransferChanged(licenseType, count, transfer);
    };
    const handleDeleteItem = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        handleTransferDeleted(transfer);
    };
    let action = <IconButton aria-label="delete" className={classes.deleteBtn} onClick={handleDeleteItem}
                             disabled={isOnlyRow}>
        <RemoveCircle/>
    </IconButton>;
    if (transfer.status === FormStatus.processing)
        action = <CircularProgress color="inherit"/>;
    else if (transfer.status === FormStatus.successful)
        action = <CheckIcon className={classes.successLabel}/>;
    const isDisabled = transfer.status === FormStatus.processing || transfer.status === FormStatus.successful;
    const raiseSelectedOrgUnitChange = (newOrgUnit: OrgUnit) => {
        handleOrgUnitChanged({newOrgUnit, id, isSource: editMode === EditMode.Source});
        setEditMode(EditMode.None);
    };
    const changeSourceOrgUnit = () => {
        setEditMode(EditMode.Source);
    };
    const changeDestinationOrgUnit = () => {
        setEditMode(EditMode.Destination);
    };
    return (<Grid item className={classes.subscriptionsTransferItem}>
        <form className={clsx(classes.form)}>
            <div className={clsx(isDisabled && classes.disabled)}>
                <List dense disablePadding>
                    <ListItem dense
                              className={classes.availableSubscriptionsLabel}>{`${t('_Shared.SubscriptionType.Pro')}: ${data.available.pro}`}</ListItem>
                    <ListItem dense
                              className={classes.availableSubscriptionsLabel}>{`${t('_Shared.SubscriptionType.Standard')}: ${data.available.standard}`}</ListItem>
                </List>
            </div>
            <FormControl disabled={isDisabled}>
                <FormLabel className={clsx(hasError('source') && classes.error)} disabled={isDisabled}>
                    {t('_Common.Labels.From')}
                </FormLabel>
                <Button disabled={isDisabled}
                        className={clsx(classes.from, hasError('source') && classes.fromButtonError)}
                        onClick={() => changeSourceOrgUnit()}>
                    {data.source.id ? data.source.name : t('Subscription.DistributeSubscriptions.SelectSource')}
                    <ArrowForwardIos/>
                </Button>
            </FormControl>
            <FormControl disabled={isDisabled}>
                <FormLabel className={clsx(hasError('destination') && classes.error)} disabled={isDisabled}>
                    {t('_Common.Labels.To')}
                </FormLabel>
                <Button disabled={isDisabled}
                        className={clsx(classes.to, hasError('destination') && classes.toButtonError)}
                        onClick={() => changeDestinationOrgUnit()}>
                    {data.destination.id ? data.destination.name : t('Subscription.DistributeSubscriptions.SelectDestination')}
                    <ArrowForwardIos/>
                </Button>
            </FormControl>
            <TextField label={t('_Shared.SubscriptionType.Pro')} className={clsx(hasError('selected') && classes.error)}
                       name='pro' value={data.selected.pro} type='number' inputProps={{min: 0, max: data.available.pro}}
                       variant='outlined' error={hasError('selected')} disabled={isDisabled}
                       onChange={handleInputChange}/>
            <TextField label={t('_Shared.SubscriptionType.Standard')}
                       className={clsx(hasError('selected') && classes.error)} name='standard'
                       value={data.selected.standard} type='number' inputProps={{min: 0, max: data.available.standard}}
                       variant='outlined' error={hasError('selected')} disabled={isDisabled}
                       onChange={handleInputChange}/>
            <div className={clsx(classes.actionContainer)}>
                {action}
            </div>
        </form>
        {children ?
            <ExpansionPanel className={classes.errorsContainer} defaultExpanded={true}>
                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>} aria-controls="panel1a-content"
                                       id="panel1a-header">
                    <WarningIcon color='error'/>
                    <Typography variant='caption'>{t('Subscription.SubscriptionAssignment.EntryNeedsAttention')}</Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    {children}
                </ExpansionPanelDetails>
            </ExpansionPanel>
            : null}

        <OrganizationUnitSelectorDialog open={editMode > 0}
                                        onSelectOrgUnit={raiseSelectedOrgUnitChange}
                                        onCancel={() => setEditMode(EditMode.None)}
                                        organizationId={organizationId}
                                        initialSelectedOrgUnit={editMode === EditMode.Source ? data.source : data.destination}
                                        titleKey={`Subscription.DistributeSubscriptions.${editMode === EditMode.Source ? 'SelectSource' : 'SelectDestination'}`}/>
    </Grid>);
};
